import { Container, Row, Col, Card, Button, Breadcrumb, Form, InputGroup, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import pageHeader from '../../assets/images/pageHeader.a8555e51.jpg';

const Donate = () => {
  const { t } = useTranslation();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState(false);

  const ZELLE_PHONE = process.env.REACT_APP_ZELLE_PHONE || '+15615263609';
  const ZELLE_PHONE_DISPLAY = '(561) 526-3609';
  const MINIMUM_DONATION = 5;

  const handlePresetAmount = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setError('');
    setShowInstructions(false);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimal point with max 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value);
      setError('');
    }
  };

  const handleContinue = async () => {
    const amount = selectedAmount || parseFloat(customAmount);

    if (!amount || isNaN(amount) || amount <= 0) {
      setError(t('donate_page.please_select_amount') as string);
      return;
    }

    if (amount < MINIMUM_DONATION) {
      setError(t('donate_page.minimum_amount_error') as string);
      return;
    }

    // Verify reCAPTCHA before showing Zelle info
    setIsVerifying(true);
    setError('');

    try {
      const recaptchaToken = await recaptchaRef.current?.executeAsync();

      if (!recaptchaToken) {
        setError(t('donate_page.recaptcha_failed') as string);
        setIsVerifying(false);
        return;
      }

      // Reset reCAPTCHA for next use
      recaptchaRef.current?.reset();

      if (customAmount) {
        setSelectedAmount(parseFloat(customAmount));
      }

      setError('');
      setShowInstructions(true);
      setIsVerifying(false);
    } catch (err) {
      setError(t('donate_page.recaptcha_error') as string);
      setIsVerifying(false);
    }
  };

  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(ZELLE_PHONE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = ZELLE_PHONE;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleChangeAmount = () => {
    setShowInstructions(false);
    setSelectedAmount(null);
    setCustomAmount('');
    setError('');
  };

  const isAmountSelected = selectedAmount !== null || (customAmount && parseFloat(customAmount) >= MINIMUM_DONATION);

  return (
    <div className="donate-page">
      {/* Page Header */}
      <div
        className="page-header py-5"
        style={{
          backgroundImage: `url(${pageHeader})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <Container>
          <h1 className="text-white">{t('donate')}</h1>
          <Breadcrumb>
            <Breadcrumb.Item href="/">{t('home')}</Breadcrumb.Item>
            <Breadcrumb.Item active>{t('donate')}</Breadcrumb.Item>
          </Breadcrumb>
        </Container>
      </div>

      <Container className="my-5">
        {!showInstructions ? (
          <>
            {/* Introduction */}
            <Row>
              <Col>
                <h2>{t('donate_page.title')}</h2>
                <p className="lead">
                  {t('donate_page.description')}
                </p>
                <Alert variant="info" className="mt-3">
                  <strong>{t('donate_page.no_fees_message')}</strong>
                </Alert>
              </Col>
            </Row>

            {/* Preset Amount Buttons */}
            <Row className="mt-4">
              <Col>
                <h4>{t('donate_page.select_amount')}</h4>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={4} className="mb-3">
                <Button
                  variant={selectedAmount === 25 ? 'success' : 'outline-primary'}
                  size="lg"
                  className="w-100 py-3"
                  onClick={() => handlePresetAmount(25)}
                >
                  <h3 className="mb-0">$25</h3>
                </Button>
              </Col>
              <Col md={4} className="mb-3">
                <Button
                  variant={selectedAmount === 50 ? 'success' : 'outline-primary'}
                  size="lg"
                  className="w-100 py-3"
                  onClick={() => handlePresetAmount(50)}
                >
                  <h3 className="mb-0">$50</h3>
                </Button>
              </Col>
              <Col md={4} className="mb-3">
                <Button
                  variant={selectedAmount === 100 ? 'success' : 'outline-primary'}
                  size="lg"
                  className="w-100 py-3"
                  onClick={() => handlePresetAmount(100)}
                >
                  <h3 className="mb-0">$100</h3>
                </Button>
              </Col>
            </Row>

            {/* Custom Amount */}
            <Row className="mt-4">
              <Col md={6} className="mx-auto">
                <Card>
                  <Card.Body>
                    <h5>{t('donate_page.custom_amount')}</h5>
                    <Form.Group className="mt-3">
                      <InputGroup size="lg">
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder={t('donate_page.custom_amount_placeholder') as string}
                          value={customAmount}
                          onChange={handleCustomAmountChange}
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        {t('donate_page.minimum_donation')}
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Error Message */}
            {error && (
              <Row className="mt-3">
                <Col md={6} className="mx-auto">
                  <Alert variant="danger">{error}</Alert>
                </Col>
              </Row>
            )}

            {/* Invisible reCAPTCHA */}
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            />

            {/* Continue Button */}
            <Row className="mt-4">
              <Col md={6} className="mx-auto text-center">
                <Button
                  variant="primary"
                  size="lg"
                  className="px-5"
                  onClick={handleContinue}
                  disabled={!isAmountSelected || isVerifying}
                >
                  {isVerifying ? t('donate_page.verifying') : t('donate_page.continue_button')}
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <>
            {/* Zelle Instructions */}
            <Row>
              <Col md={8} className="mx-auto">
                <Card className="border-success">
                  <Card.Header className="bg-success text-white">
                    <h3 className="mb-0">{t('donate_page.zelle_instructions_title')}</h3>
                  </Card.Header>
                  <Card.Body>
                    {/* Donation Amount */}
                    <div className="text-center mb-4 p-3 bg-light rounded">
                      <h5 className="text-muted mb-2">{t('donate_page.donation_amount')}</h5>
                      <h2 className="text-success mb-0">${selectedAmount?.toFixed(2)}</h2>
                    </div>

                    {/* Recipient Name */}
                    <div className="mb-4 p-3 border rounded">
                      <h5 className="mb-2">{t('donate_page.zelle_recipient_name')}</h5>
                      <h4 className="mb-0">Ychemy Saintil</h4>
                    </div>

                    {/* Zelle Phone Number */}
                    <div className="mb-4 p-3 border rounded">
                      <h5 className="mb-3">{t('donate_page.zelle_phone')}</h5>
                      <div className="d-flex align-items-center gap-3">
                        <h4 className="mb-0 flex-grow-1">{ZELLE_PHONE_DISPLAY}</h4>
                        <Button
                          variant={copied ? 'success' : 'primary'}
                          onClick={handleCopyPhone}
                        >
                          {copied ? '✓ ' + t('donate_page.copied') : t('donate_page.copy_phone')}
                        </Button>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="mb-4">
                      <h5 className="mb-3">{t('donate_page.instructions_title')}</h5>
                      <ol className="ps-3">
                        <li className="mb-2">{t('donate_page.instruction_1')}</li>
                        <li className="mb-2">{t('donate_page.instruction_2')}</li>
                        <li className="mb-2">{t('donate_page.instruction_3')}</li>
                        <li className="mb-2">{t('donate_page.instruction_4')}</li>
                        <li className="mb-2">{t('donate_page.instruction_5')}</li>
                        <li className="mb-2">{t('donate_page.instruction_6')}</li>
                      </ol>
                    </div>

                    {/* Thank You Message */}
                    <Alert variant="success" className="mb-3">
                      <h5 className="mb-2">✝️ {t('donate_page.thank_you_message')}</h5>
                      <p className="mb-0"><em>{t('donate_page.instant_transfer')}</em></p>
                    </Alert>

                    {/* Change Amount Button */}
                    <div className="text-center">
                      <Button
                        variant="outline-secondary"
                        onClick={handleChangeAmount}
                      >
                        {t('donate_page.change_amount')}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default Donate;
